/* eslint-disable no-param-reassign */
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState } from '../stores/types/types';
import { BROWSER_TYPES } from '../types/constants';
import { getBrowserType } from '../utils/utils';
import { URL_STRINGS } from '../stores/types/session';
import { ECHO_CANCEL_SOURCE_VOLUME } from '../types/constants';
import { useAudioSink } from './devices/use-audio-sink';
import logger from '../utils/logger-rum';

interface RTCConnectionMap {
	[key: string]: RTCPeerConnection;
}

const audioRTCConnections: RTCConnectionMap = {};
const audioLoopbackConnections: RTCConnectionMap = {};

export const shouldDoEchoCancel = (string?: string | undefined): boolean => {
	const url = string ?? window.location.pathname;
	// we don't want echo cancellation if someone
	// is using the program feed, or onsite user features
	if (
		url?.includes(URL_STRINGS.ONSITE)
		|| url?.includes(URL_STRINGS.STREAM)
		|| url?.includes(URL_STRINGS.EMBED)
	) {
		return false;
	}
	return true;
};

const getRTCConnection = (id: string, connectionValue: RTCConnectionMap) => {
	if (connectionValue[id]) {
		connectionValue[id].close();
		delete connectionValue[id];
	}
	if (!connectionValue[id]) {
		connectionValue[id] = new RTCPeerConnection();
	}
	return connectionValue[id];
};

const doEchoCancel = async (videoElement: HTMLVideoElement | HTMLAudioElement, audioElement: HTMLAudioElement) => {
	try {
		if (!videoElement) return;

		if (process.env.REACT_APP_NODE_ENV === 'dev') {
			// eslint-disable-next-line no-console
			console.log('[BDEV-5171] doEchoCancel invoked (RTC loopback path active)', {
				at: new Date().toISOString(), videoId: (videoElement as any).id, audioId: audioElement.id,
			});
		}

		const isFirefox = getBrowserType() === BROWSER_TYPES.FIREFOX;

		if (!isFirefox) {
			videoElement.volume = ECHO_CANCEL_SOURCE_VOLUME;
		}

		const videoElementCasted = videoElement as any;

		let stream;

		if (!isFirefox && videoElementCasted.captureStream) {
			stream = videoElementCasted.captureStream();
		} else if (isFirefox) {
			stream = videoElementCasted.mozCaptureStream();
		}

		if (!stream?.getAudioTracks().length) {
			return;
		}

		const rtcConnection = getRTCConnection(audioElement.id, audioRTCConnections);
		const rtcLoopbackConnection = getRTCConnection(audioElement.id, audioLoopbackConnections);
		const loopbackStream = new MediaStream();

		rtcConnection.onicecandidate = (e) => (
			e.candidate && rtcLoopbackConnection.addIceCandidate(new RTCIceCandidate(e.candidate))
		);
		rtcLoopbackConnection.onicecandidate = (e) => (
			e.candidate && rtcConnection.addIceCandidate(new RTCIceCandidate(e.candidate))
		);

		rtcLoopbackConnection.ontrack = (e) => (
			e.streams[0].getTracks().forEach((track) => loopbackStream.addTrack(track))
		);

		const audioStream = new MediaStream();

		audioStream.addTrack(stream.getAudioTracks()[0]);

		let destStream = audioStream;
		const ref = audioElement as any;
		if (ref.createContext) {
			var dest = ref.createContext(audioStream);
			destStream = dest.stream;
		}

		(rtcConnection as any).addStream(destStream);

		const offerOptions = {
			offerVideo: true,
			offerAudio: true,
			offerToReceiveAudio: false,
			offerToReceiveVideo: false,
		};

		const offer = await rtcConnection.createOffer(offerOptions);

		if (offer?.sdp) {
		// set offer's audio codec description to prefer stereo
			offer.sdp = offer.sdp.replace('useinbandfec=1', 'useinbandfec=1; stereo=1');
		}

		await rtcConnection.setLocalDescription(offer);
		await rtcLoopbackConnection.setRemoteDescription(offer);
		const answer = await rtcLoopbackConnection.createAnswer();

		if (answer?.sdp) {
		// set answer's audio codec description to prefer stereo
			answer.sdp = answer.sdp.replace('useinbandfec=1', 'useinbandfec=1; stereo=1');
		}

		await rtcLoopbackConnection.setLocalDescription(answer);
		await rtcConnection.setRemoteDescription(answer);

		audioElement.srcObject = loopbackStream;
		await audioElement.play();

		const onPauseFunction = () => {
			if (audioElement) {
				audioElement.pause();
				rtcConnection.close();
				delete audioRTCConnections[audioElement.id];
				rtcLoopbackConnection.close();
				delete audioLoopbackConnections[audioElement.id];
			}
		};
		videoElement.onpause = onPauseFunction;
	} catch (e) {
		logger.error(e);
	}
};

// no need to recalc each render
const savedShouldDoEchoCancel = shouldDoEchoCancel();

type EchoCancellationMediaRef =
	| React.RefObject<HTMLVideoElement | null>
	| React.RefObject<HTMLAudioElement | null>;

const useEchoCancellation = (
	videoElRef: EchoCancellationMediaRef,
	echoCancelledAudioRef: React.RefObject<HTMLAudioElement | null>,
) => {
	const {
		debugEnabled,
		debugEchoCancellationEnabled,
	} = useSelector((state: GreenroomState) => state.DebugReducer);

	const { applySinkId } = useAudioSink();
	const echoDebounce = React.useRef<NodeJS.Timeout | null>(null);

	// Apply setSinkId to echo cancelled audio element (where sound actually comes out)
	useEffect(() => {
		if (echoCancelledAudioRef.current) {
			applySinkId(echoCancelledAudioRef.current);
		}
	}, [applySinkId, echoCancelledAudioRef.current]);

	useEffect(() => {
		if (process.env.REACT_APP_NODE_ENV === 'dev') {
			// eslint-disable-next-line no-console
			console.log('[BDEV-5171] echo-cancellation gate check', {
				pathname: window.location.pathname,
				savedShouldDoEchoCancel,
				debugEnabled,
				debugEchoCancellationEnabled,
				hasVideoEl: !!videoElRef.current,
				hasAudioEl: !!echoCancelledAudioRef.current,
				gatePasses: !!(
					videoElRef.current
					&& echoCancelledAudioRef.current
					&& (!debugEnabled || debugEchoCancellationEnabled)
					&& savedShouldDoEchoCancel
				),
			});
		}
		if (
			videoElRef.current
			&& echoCancelledAudioRef.current
			&& (!debugEnabled || debugEchoCancellationEnabled)
			&& savedShouldDoEchoCancel
		) {
			// occasionally a video will start playing before the 'onplay' handler is set
			if (!videoElRef.current.paused) {
				if (echoDebounce.current) { clearTimeout(echoDebounce.current); }
				echoDebounce.current = setTimeout(() => {
					if (videoElRef.current && echoCancelledAudioRef.current) {
						doEchoCancel(videoElRef.current, echoCancelledAudioRef.current);
					}
				}, 1);
			}

			const doEchoCheckAndApply = () => {
				if (echoDebounce.current) { clearTimeout(echoDebounce.current); }
				echoDebounce.current = setTimeout(() => {
					if (videoElRef.current && echoCancelledAudioRef.current) {
						doEchoCancel(videoElRef.current, echoCancelledAudioRef.current);
					}
				}, 1);
			};
			videoElRef.current.onplay = doEchoCheckAndApply;
		}
	}, [videoElRef.current,
		echoCancelledAudioRef.current,
		debugEnabled,
		debugEchoCancellationEnabled]);
};

export default useEchoCancellation;
